import React, { useState, useEffect, useRef } from "react";
import { Button, Select, Table, message, Spin, Tooltip, Popconfirm, Tag, Affix, Progress, Modal } from "antd";
import { DeleteOutlined, ThunderboltOutlined, FileOutlined } from "@ant-design/icons";
import styled, { createGlobalStyle } from "styled-components";
import {
  saveICFSelectedSection,
  getICFTemplateList,
  getICFSectionConfiguredDataset,
  triggerICFPlanner,
  saveICFGeneratedLog,
  getICFGeneratedLog,
  getICFContextLib,
  getStudyProcedure,
} from "../../../services/icfService";
import { insertTemplateIntoWord } from "../../taskpane";
import { Frontend_LOCAL_PROXY } from "../../../../constant";
import { GrDocumentConfig, GrDocumentDownload } from "react-icons/gr";

const { Option } = Select;

// Global font
const GlobalStyle = createGlobalStyle`
  * {
    font-family: 'Open Sans', sans-serif !important;
  }
`;

const StyledButton = styled(Button)`
  background: #e3ecff;
  color: #426bba !important;
  font-weight: 600;
  font-size: 14px;
  border: none;
  border-radius: 1rem;
  box-shadow: 0 2px 6px rgba(66, 107, 186, 0.2);
  padding: 4px 14px;
  margin-bottom: 14px;

  &:hover {
    background: #d7e4ff !important;
    color: #426bba !important;
  }
`;
export const StyledSelect = styled(Select)`
  width: 100%;
  height: 32px;
  font-size: 14px;
  font-weight: 600;
  color: #426bba;
  border-radius: 1rem !important;
  box-shadow: 0 2px 6px rgba(66, 107, 186, 0.2);
  text-align: center;

  && .ant-select-selector {
    background-color: #e3ecff !important;
    font-size: 14px;
    font-weight: 600;
    border: none !important;
    border-radius: 1rem !important;
    padding: 4px 16px;
    color: #426bba !important;
    text-align: center;
  }

  && .ant-select-selection-item,
  && .ant-select-selection-placeholder {
    font-size: 14px;
    font-weight: 600;
    color: #426bba !important;
    text-align: center;
  }

  && .ant-select-arrow {
    color: #426bba !important;

    svg {
      fill: #426bba !important;
    }
  }

  && .ant-select-dropdown {
    background-color: #e3ecff;
    color: #426bba;
    border-radius: 1rem;
  }

  && .ant-select-item-option-content {
    color: #426bba;
  }

  &:hover .ant-select-selector {
    background-color: #d7e4ff !important;
    color: #426bba !important;
  }
`;
export const StyledTableWrapper = styled.div`
  .ant-table-thead > tr > th {
    background: #426bba !important;
    border: 1px solid #426bba;
    color: #fefeff !important;
    font-weight: 600;
    font-size: 12px;
  }
  .ant-table-tbody > tr > td {
    background: linear-gradient(
      150deg,
      rgba(235, 240, 255, 1) 25%,
      rgba(225, 235, 255, 1) 50%,
      rgba(215, 230, 255, 1) 100%
    );
    border: 1px solid #bfd3f2;
    color: #2c4c91;
    font-size: 11px;
    padding: 6px 12px;
  }
`;
const StyledProgress = styled(Progress)`
  .ant-progress-bg {
    background-color: #426bba !important; /* progress bar color */
  }

  .ant-progress-text {
    font-size: 11px !important; /* text size */
    color: #426bba !important; /* text color */
  }
`;
const FileNameTag = ({ fileName, showIconOnly = false }) => {
  return (
    <Tooltip title={fileName}>
      <Tag
        color="blue"
        style={{
          fontSize: 12,
          display: "flex",
          alignItems: "center",
          maxWidth: 200,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          flex: 1,
          cursor: "pointer",
        }}
      >
        <FileOutlined />
        {!showIconOnly && (
          <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              flex: 1,
            }}
          >
            {fileName}
          </span>
        )}
      </Tag>
    </Tooltip>
  );
};

const TemplatePrompt = ({
  onBack,
  protocolId,
  docId,
  jobId,
  docVersion,
  productName,
  userMetadata,
  procedures,
  userEmail,
  userId,
  fileList,
}) => {
  const frontEndUrl = Frontend_LOCAL_PROXY;
  const [loading, setLoading] = useState(false);
  const [rowLoader, setRowLoader] = useState(false);
  const [rowLoading, setRowLoading] = useState(null);
  const [templateId, setTemplateId] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [systemMetadata, setSystemMetadata] = useState({});
  const [side_effects, setSideEffects] = useState([]);
  const [sections, setSections] = useState([]);
  const [history, setHistory] = useState({});
  const [templateLoad, setTemplateLoad] = useState(false);
  const [generateAllLoading, setGenerateAllLoading] = useState(false);
  const [generateProgress, setGenerateProgress] = useState(0);
  const [completedCount, setCompletedCount] = useState(0);
  const [selectedRunIndexes, setSelectedRunIndexes] = useState({});

  const mismatchedKeysRef = useRef([]);
  const mismatchWarningShownRef = useRef(false);
  const abortControllerRef = useRef(null);
  const cancelGenerationRef = useRef(false);

  const abortControllerHandle = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort(); // cancels the request
    }
  };

  const fetchConfiguredDataset = async (template_config_id) => {
    try {
      setLoading(true);
      const dataset = await getICFSectionConfiguredDataset({ template_config_id });
      // console.log("Configured Dataset", dataset);
      setSections(dataset?.data?.section_list || []);
    } catch {
      message.error("Failed to load template dataset");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let isMounted = true;
    (async () => {
      setTemplateLoad(true);
      try {
        const res = await getICFTemplateList({
          doc_id: docId,
          doc_version: docVersion,
          product_name: productName,
        });
        // console.log("Template List", res);
        const list = res?.data?.template_list || [];
        const metaDataObj = res?.data?.metadata || {};
        if (!isMounted) return;

        setTemplates(list);
        setSystemMetadata(metaDataObj);
        setSideEffects(res?.data?.side_effects || []);
        //Auto-select if there’s exactly one template
        if (list.length === 1) {
          const onlyTemplate = list[0];
          setTemplateId(onlyTemplate.template_id);
          setLoading(true);
          message.info(`Auto selected: ${onlyTemplate.template_name}`);
          await fetchConfiguredDataset(onlyTemplate.template_id);
        }
      } catch {
        message.error("Failed to load ICF Template");
      } finally {
        if (isMounted) setTemplateLoad(false);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, []);

  const handleTemplateSelect = async (val, option) => {
    if (val === templateId) return;

    setTemplateId(val);
    await fetchConfiguredDataset(val);
  };

  const handlePreviewButtonClick = (section) => {
    Office.onReady(() => {
      if (Office.context.ui) {
        Office.context.ui.displayDialogAsync(
          `${frontEndUrl}/GenerateTextDialog.html`,
          { height: 85, width: 90 },
          (result) => {
            if (result.status === Office.AsyncResultStatus.Failed) {
              console.error("Dialog open failed:", result.error);
              return;
            }

            const dialog = result.value;
            dialog.addEventHandler(Office.EventType.DialogMessageReceived, (args) => {
              try {
                const data = JSON.parse(args.message);
                // console.log("Message from dialog:", data);

                if (data.action === "ready") {
                  const sectionId = section?.section_config_id;
                  const sectionHistory = history[sectionId] || [];
                  setTimeout(() => {
                    dialog.messageChild(
                      JSON.stringify({
                        action: "init",
                        payload: {
                          section: {
                            ...section,
                            protocolId,
                            jobId,
                            docVersion,
                            procedures,
                            userEmail,
                            userId,
                            userMetadata,
                            systemMetadata,
                            side_effects,
                          },
                          history: { [sectionId]: sectionHistory },
                        },
                      })
                    );
                  }, 500);
                }

                if (data?.action === "close") {
                  const { history: childHistory, selectedIndex, sectionId } = data?.payload;
                  // Replace history entirely with latest childHistory
                  setHistory((prev) => ({
                    ...prev,
                    ...childHistory,
                  }));
                  // setHistory((prev) => {
                  //   const sectionId = Object.keys(childHistory)[0];
                  //   const newRuns = childHistory[sectionId] || [];
                  //   const prevRuns = prev[sectionId] || [];
                  //   return {
                  //     ...prev,
                  //     [sectionId]: [...newRuns, ...prevRuns],
                  //   };
                  // });
                  setSelectedRunIndexes((prev) => ({
                    ...prev,
                    [sectionId]: selectedIndex ?? 0,
                  }));
                  dialog.close();
                }
              } catch (err) {
                console.error("Error parsing dialog message:", err);
              }
            });
          }
        );
      }
    });
  };

  const handleSaveLog = async (section, generatedText) => {
    try {
      await saveICFGeneratedLog({
        protocol_id: protocolId,
        section_name: section.section_name,
        generated_text: generatedText,
      });
    } catch {
      message.error("Failed to save generated log");
    }
  };

  //Uncomment if need to pass user selected attributes
  // const userMetadataText = Array.isArray(userMetadata)
  //   ? userMetadata
  //       .filter((item) => item?.deletable)
  //       .map((item) => `${item.key}: ${item.value}.`)
  //       .join(" ")
  //   : "";

  // const systemMetadataText =
  //   systemMetadata && typeof systemMetadata === "object" && !Array.isArray(systemMetadata)
  //     ? Object.entries(systemMetadata)
  //         .map(([key, value]) => (value !== undefined && value !== null ? `${key}: ${value}.` : ""))
  //         .filter(Boolean)
  //         .join(" ")
  //     : "";

  // const sideEffectsText = Array.isArray(side_effects) ? side_effects.flat().join(" ") : "";

  const checkMetadataMismatch = () => {
    const userMetadataMap = Object.fromEntries(userMetadata.filter((m) => m.deletable).map((m) => [m.key, m.value]));

    const mismatched = [];

    const mergedMetadataEntries = Object.entries(systemMetadata).map(([key, sysValue]) => {
      const userValue = userMetadataMap[key];

      if (userValue !== undefined) {
        const normalizedSys = String(sysValue).trim().toLowerCase();
        const normalizedUser = String(userValue).trim().toLowerCase();

        if (normalizedSys === normalizedUser) {
          // Values Match
          return `${key}: ${sysValue}.`;
        } else {
          // Values Mismatch
          mismatched.push(key);
          return `${key}: ${userValue}.`;
        }
      }

      // Key not in userMetadata so keep system’s value
      return `${key}: ${sysValue}.`;
    });

    const finalMetadataText = mergedMetadataEntries.filter(Boolean).join(" ");
    mismatchedKeysRef.current = mismatched;
    return finalMetadataText;
  };

  const buildICFPayload = (section, finalMetadataText, sideEffectsText) => {
    // const fullTask = [section?.task || "", systemMetadataText, sideEffectsText].filter(Boolean).join(" ");
    const fullTask = [section?.task || "", finalMetadataText, sideEffectsText].filter(Boolean).join(" ");

    return {
      section_name: section?.section_name || "",
      objective:
        section?.objective + (section?.html_template ? ` $$template$$ ${section?.html_template} $$template$$` : "") ||
        "",
      task: fullTask,
      // section?.task ? `${section.task}${metadataText ? " " + metadataText : ""}` : "",
      business_rule: Array.isArray(section?.rule_defs) ? section?.rule_defs.join(", ") : "",
      report_label: "Informed Consent Form (ICF)",
      documents: [
        {
          doc_id: docId,
          document_version: docVersion,
          all_sections: "false",
          selected_list: section?.context_sections,
        },
      ],
      request_id: `refresh_id_icf_${Date.now()}_${section?.section_name}_${section?.section_config_id}`,
      username: userEmail,
    };
  };

  const buildICFContextPayload = (section) => {
    return {
      section_name: section?.section_name?.replace(/^\s*([0-9]+(\.[0-9]+)*|[IVXLCDM]+)\.?\s*/i, "") || "",
      procedure_names: procedures,
    };
  };

  const getEffectiveMetadata = () => {
    const userMetadataMap = Object.fromEntries(userMetadata.filter((m) => m.deletable).map((m) => [m.key, m.value]));

    const effective = {};
    const mismatched = [];

    Object.entries(systemMetadata).forEach(([key, sysValue]) => {
      const userValue = userMetadataMap[key];

      if (userValue !== undefined) {
        const normalizedSys = String(sysValue).trim().toLowerCase();
        const normalizedUser = String(userValue).trim().toLowerCase();

        if (normalizedSys === normalizedUser) {
          // match → use system value
          effective[key] = sysValue;
        } else {
          // mismatch then prefer user value
          mismatched.push(key);
          effective[key] = userValue;
        }
      } else {
        //use system
        effective[key] = sysValue;
      }
    });

    mismatchedKeysRef.current = mismatched;

    return { effective };
  };

  const buildProcedurePayload = (section) => {
    const { effective } = getEffectiveMetadata();
    return {
      task: {
        study_type: effective?.study_type,
        is_healthy_participants: effective?.is_healthy_participants,
        region: effective?.region,
      },
      doc_id: docId,
      doc_version: docVersion,
      section_name: section?.section_name || "",
      procedures,
    };
  };

  const handleGenerateForSection = async (section, index = null) => {
    if (index !== null) {
      setRowLoading(index);
      setRowLoader(true);
    }
    if (cancelGenerationRef.current) {
      console.warn("Generation aborted before API call.");
      return;
    }
    abortControllerRef.current = new AbortController();

    try {
      let generatedText = "";
      let payload = "";

      if (section?.standard_flag === "true" && section?.standard_contextlib === "false") {
        generatedText = section?.standard_text ?? "";
        // console.log("Using standard_text (skipped planner API):", section?.standard_text);
      } else if (section?.standard_flag === "true" && section?.standard_contextlib === "true") {
        payload = buildICFContextPayload(section);
        // console.log("ICF Context Library Payload:", payload);
        const res = await getICFContextLib(payload, abortControllerRef.current.signal);
        // console.log("Standard Context Library Api Response", res);
        if (Array.isArray(res)) {
          generatedText = `<div class="procedure-list"><ul>${res
            .map((item) => `<li><strong>${item["PROCEDURE NAME"]}</strong>: ${item["DESCRIPTION"]}</li>`)
            .join("")}</ul></div>`;
        } else {
          generatedText = "No context library data available.";
        }
      } else {
        if (section?.standard_generation === "false") {
          payload = buildProcedurePayload(section);

          if (mismatchedKeysRef.current.length > 0 && !mismatchWarningShownRef.current) {
            message.warning(
              `Attribute ${mismatchedKeysRef.current.join(", ")} selected by the user does not match the attribute detected by the system`,
              5
            );
            mismatchWarningShownRef.current = true;
          }

          // console.log("ICF Payload:", payload);
          const res = await getStudyProcedure(payload, abortControllerRef.current.signal);
          // console.log("Study procedure API Response", res);
          generatedText = res || "Unable to Generate Text";
        } else {
          let sideEffectsText = "";

          if (section?.standard_side_effects === "true" && Array.isArray(side_effects)) {
            sideEffectsText = side_effects.flat().join(" ");
          }
          const finalMetadataText = checkMetadataMismatch();

          payload = buildICFPayload(section, finalMetadataText, sideEffectsText);

          if (mismatchedKeysRef.current.length > 0 && !mismatchWarningShownRef.current) {
            message.warning(
              `Attribute ${mismatchedKeysRef.current.join(", ")} selected by the user does not match the attribute detected by the system`,
              5
            );
            mismatchWarningShownRef.current = true;
          }
          // console.log("ICF Payload:", payload);
          const res = await triggerICFPlanner(payload, abortControllerRef.current.signal);
          // console.log("Planner Api Response", res);
          generatedText = res?.data?.generatedText || "Unable to Generate Text";
        }
      }
      // await handleSaveLog(section, generatedText);

      // Use section_config_id as key
      const sectionId = section?.section_config_id;
      const prevRuns = history[sectionId] || [];
      const newRun = {
        key: Date.now(),
        payload,
        objective: section?.objective || "",
        generatedText,
        business_rule: Array.isArray(section?.rule_defs) ? section?.rule_defs.join(", ") : "",
        timestamp: new Date().toLocaleString(),
        author: userEmail,
        source: "protocol",
      };

      setHistory((prev) => ({
        ...prev,
        [sectionId]: [newRun, ...prevRuns],
      }));

      message.success(`Generated text for ${section?.section_name}`);

      if (prevRuns?.length === 0) {
        await insertTemplateIntoWord({ ...section, generatedText }, protocolId);
      }
    } catch (err) {
      if (err.name === "AbortError") {
        console.warn(`Generation aborted for section: ${section?.section_name}`);
        message.warning(`Generation canceled for ${section?.section_name}`);
      } else {
        console.error(err);
        message.error(`Failed to generate text`);
      }
    } finally {
      if (index !== null) {
        setRowLoading(null);
        setRowLoader(false);
      }
    }
  };

  const handleInsertIntoWord = async (section) => {
    const sectionId = section?.section_config_id;
    const selectedIndex = selectedRunIndexes[sectionId] ?? 0;

    const selectedRun = history[sectionId]?.[selectedIndex];
    if (!selectedRun) {
      message.warning("No generated text available to insert");
      return;
    }
    await insertTemplateIntoWord({ ...section, generatedText: selectedRun?.generatedText }, protocolId);
  };

  const handleBack = () => {
    const isFileListEmpty = fileList.length === 0;
    const modalTitle = isFileListEmpty ? "Are you sure?" : "Discard Changes?";
    const modalContent = isFileListEmpty
      ? "Do you want to go back?"
      : "Your data will be discarded if you go back. Are you sure?";

    Modal.confirm({
      title: modalTitle,
      content: modalContent,
      okText: "Yes, Go Back",
      cancelText: "Stay",
      okType: "danger",
      centered: true,
      onOk() {
        abortControllerHandle();
        //Cancel current API request
        abortControllerRef.current?.abort();
        //Stop the loop
        cancelGenerationRef.current = true;
        onBack?.();
        setTemplates([]);
        setSections([]);
        setHistory({});
        setSelectedRunIndexes({});
        setTemplateId(null);
        setGenerateProgress(0);
        setCompletedCount(0);
      },
      onCancel() {
        console.log("Stay on page");
      },
    });
  };

  const columns = [
    {
      title: "Section",
      dataIndex: "section_name",
      key: "section_name",
      width: "80%",
    },
    {
      title: "Action",
      key: "action",
      width: "20%",
      render: (_, section, index) => {
        const hasHistory = history?.[section?.section_config_id]?.length > 0;
        const isGenerating = rowLoading === index;

        return (
          <div style={{ display: "flex", alignItems: "center" }}>
            {isGenerating ? (
              <Spin size="small" style={{ marginRight: 6, marginLeft: 6 }} />
            ) : hasHistory ? (
              <Button
                icon={
                  <Tooltip placement="top" title="Insert Latest into Word">
                    <GrDocumentDownload />
                  </Tooltip>
                }
                size="small"
                style={{ marginRight: "5px", color: "#27a6a4" }}
                onClick={() => handleInsertIntoWord(section)}
                type="text"
              />
            ) : (
              <Button
                icon={
                  <Tooltip placement="top" title="Generate Text">
                    <ThunderboltOutlined />
                  </Tooltip>
                }
                size="small"
                style={{ marginRight: "5px", color: "#426bba" }}
                onClick={() => handleGenerateForSection(section, index)}
                type="text"
                disabled={loading || rowLoader}
              />
            )}
            {/* Preview */}
            <Button
              icon={
                <Tooltip placement="top" title="Click to view Section Details">
                  <GrDocumentConfig />
                </Tooltip>
              }
              size="small"
              style={{
                marginRight: "5px",
                color: "#426bba",
              }}
              onClick={() => handlePreviewButtonClick(section)}
              type="text"
              disabled={loading || isGenerating}
            />

            {/* Delete */}
            {/* <Popconfirm
            title="Are you sure you want to delete this section?"
            onConfirm={() => {}}
            okButtonProps={{ style: { background: "#27a6a4" } }}
            okText="Yes"
            cancelText="No"
          >
            <Button danger icon={<DeleteOutlined />} size="small" />
          </Popconfirm> */}
          </div>
        );
      },
    },
  ];

  return (
    <div style={{ padding: "12px" }}>
      <GlobalStyle />

      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          marginBottom: "8px",
          gap: "2rem",
        }}
      >
        {/* Back */}
        <StyledButton onClick={handleBack} disabled={loading}>
          Back
        </StyledButton>
        {fileList?.[0]?.name && <FileNameTag fileName={fileList?.[0]?.name} />}
      </div>

      {generateAllLoading ? (
        <Affix offsetTop={2}>
          <div
            style={{
              background:
                "linear-gradient(150deg, rgba(235, 240, 255, 1) 25%, rgba(225, 235, 255, 1) 50%, rgba(215, 230, 255, 1) 100%)",
              padding: "8px 16px",
              color: "#426BBA",
              zIndex: 1000,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              background: "#e3ecff",
              color: "#426bba !important",
              fontSize: "12px",
              border: "none",
              borderRadius: "1rem",
              boxShadow: "0 2px 6px rgba(66, 107, 186, 0.2)",
              padding: "4px 10px",
              margin: "auto",
              marginBottom: "15px",
            }}
          >
            <div style={{ marginTop: 0, color: "#426bba", fontSize: "11px", marginRight: 3 }}>
              {completedCount}/{sections?.length || 0} sections generated
            </div>
            <div style={{ width: "35%", marginRight: 3, fontWeight: 600 }}>
              <StyledProgress
                percent={generateProgress}
                status={generateProgress === 100 ? "success" : "active"}
                strokeColor="#426bba"
                style={{ color: "#426bba", fontSize: "12px" }}
              />
            </div>
            <div style={{ overflow: "hidden" }}>
              {fileList?.[0]?.name && <FileNameTag fileName={fileList?.[0]?.name} showIconOnly={true} />}
            </div>
          </div>
        </Affix>
      ) : null}

      {/* Template dropdown */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          marginBottom: "12px",
        }}
      >
        <StyledSelect
          placeholder="Select Template"
          value={templateId}
          onChange={handleTemplateSelect}
          loading={templateLoad}
          disabled={rowLoader || templateLoad}
        >
          {templates.map((tpl) => (
            <Option key={tpl.template_id} value={tpl.template_id}>
              {tpl.template_name}
            </Option>
          ))}
        </StyledSelect>

        {/* Use Template */}
        {/* <StyledButton
          disabled={!templateId}
          onClick={() => insertTemplateIntoWord(sections?.section_name || [])}
        >
          Use Template
        </StyledButton> */}
      </div>

      {/* Generate All right after Use Template */}
      {templateId && (
        <div
          style={{
            display: "grid",
            marginBottom: 10,
            marginTop: 20,
          }}
        >
          <StyledButton
            onClick={async () => {
              if (!sections?.length) {
                message.warning("No sections available to generate.");
                return;
              }

              setGenerateAllLoading(true);
              setCompletedCount(0);
              setGenerateProgress(0);
              message.info("Generating for all sections...");
              // Reset cancel flag before starting
              cancelGenerationRef.current = false;
              const totalSections = sections.length;

              try {
                for (let i = 0; i < totalSections; i++) {
                  if (cancelGenerationRef.current) {
                    message.warning("Generation stopped by user.");
                    break;
                  }

                  await handleGenerateForSection(sections[i], i);

                  if (cancelGenerationRef.current) {
                    message.warning("Generation stopped by user.");
                    break;
                  }
                  await new Promise((r) => setTimeout(r, 500));
                  setCompletedCount((prev) => prev + 1);
                  setGenerateProgress(Math.round(((i + 1) / totalSections) * 100));
                }
                if (!cancelGenerationRef.current) {
                  message.success("All sections processed successfully!");
                }
              } catch (error) {
                console.error("Error while generating sections:", error);
                message.error("An error occurred during generation. Please try again.");
              } finally {
                setGenerateAllLoading(false);
              }
            }}
            disabled={loading || rowLoader || generateAllLoading}
          >
            Generate All
          </StyledButton>
        </div>
      )}

      {/* Sections Table */}
      {templateId && (
        <Spin spinning={loading} tip="Loading Sections, please wait...">
          <StyledTableWrapper>
            <Table
              dataSource={sections?.map((s, i) => ({ key: i, ...s })) || []}
              columns={columns}
              pagination={false}
              size="small"
            />
          </StyledTableWrapper>
        </Spin>
      )}
    </div>
  );
};

export default TemplatePrompt;
